import { asActivityLabel } from "@/lib/activity-label";
import { isTauri } from "@/lib/is-tauri";
import {
  publishCompactResourceLifecycle,
  publishCompactResourceThumbnail,
} from "./compact-resource-events";
import type { ScreenshotPreview } from "./use-screenshot-capture";
import { getWindowRoleSync } from "./use-window-role";
import { translate } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

function canPublish(): boolean {
  return !isTauri() || getWindowRoleSync() === "owner";
}

export function publishExecutionResourceFrame(
  data: Record<string, unknown>,
): void {
  if (!canPublish()) return;
  const resource = data.resource as
    | {
        kind?: unknown;
        key?: unknown;
        label?: unknown;
        label_key?: unknown;
        route?: unknown;
        app_name?: unknown;
      }
    | undefined;
  const executionId = data.execution_id;
  const toolUseId = data.tool_use_id;
  const phase = data.phase;
  if (
    typeof executionId !== "string" ||
    typeof toolUseId !== "string" ||
    (phase !== "started" && phase !== "finished") ||
    (resource?.kind !== "app" &&
      resource?.kind !== "screenshot" &&
      resource?.kind !== "action") ||
    typeof resource.key !== "string" ||
    typeof resource.label !== "string"
  ) {
    return;
  }
  const route =
    resource.route === "ax" || resource.route === "mcp"
      ? resource.route
      : undefined;
  const activityLabel = asActivityLabel(resource.label_key);
  const originDisplayId =
    typeof data.origin_display_id === "number" &&
    Number.isInteger(data.origin_display_id) &&
    data.origin_display_id >= 0 &&
    data.origin_display_id <= 0xffffffff
      ? data.origin_display_id
      : undefined;
  publishCompactResourceLifecycle({
    executionId,
    ...(originDisplayId !== undefined ? { originDisplayId } : {}),
    toolUseId,
    phase,
    ...(phase === "finished" && data.failed === true ? { failed: true } : {}),
    resource: {
      kind: resource.kind,
      key: resource.key,
      label: resource.label,
      ...(activityLabel ? { activityLabel } : {}),
      ...(route ? { route } : {}),
      ...(phase === "finished" &&
      data.failed !== true &&
      resource.kind === "app" &&
      route === "ax" &&
      resource.key === "target" &&
      typeof resource.app_name === "string" &&
      resource.app_name.trim() &&
      resource.app_name.length <= 200 &&
      Array.from(resource.app_name).every(
        (char) => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127,
      )
        ? { appName: resource.app_name.trim() }
        : {}),
    },
  });
}

export function beginCompactPromptScreenshotPreview(
  clientRequestId: string,
  previews: ScreenshotPreview[],
): void {
  // A prompt is submitted by one window, which may be a secondary display.
  // Only server-broadcast execution events need the owner deduplication gate.
  if (previews.length === 0) return;
  previews.forEach((preview, index) =>
    publishCompactResourceThumbnail({
      executionId: clientRequestId,
      captureId: `prompt:${clientRequestId}`,
      displayId: preview.displayId,
      ordinal: index,
      visibility: "all-displays",
      jpegBase64: preview.jpegBase64,
    }),
  );
  publishCompactResourceLifecycle({
    executionId: clientRequestId,
    toolUseId: "prompt-screenshot",
    phase: "started",
    resource: {
      kind: "screenshot",
      key: "screenshot",
      label: translate(compactMessages, "screenshot"),
    },
  });
}

export function bindCompactPromptScreenshot(
  clientRequestId: string | undefined,
  executionId: string,
): void {
  if (!canPublish() || !clientRequestId) return;
  publishCompactResourceLifecycle({
    executionId,
    clientRequestId,
    phase: "execution_bound",
  });
}

export function finishCompactResources(executionId: string): void {
  publishCompactResourceLifecycle({
    executionId,
    phase: "execution_finished",
  });
}
